// ─── components/Support/AskUniFreelancer.jsx ─────────────────────────────────
// Floating "Ask UniFreelancer" support widget — answers general platform
// questions (fees, escrow, taxes, disputes) and can look up the viewer's OWN
// orders/contracts, balance, and tax status (never anyone else's — see the
// zero-parameter tool design in services/aiService.js). Nothing here is
// persisted: the conversation lives in this component's state only and is
// gone on refresh, matching what the widget tells the user up front.
//
// Bottom-RIGHT by design — NeedsYouTracker owns bottom-left
// (components/Notifications/NeedsYouTracker.jsx), and the two are unrelated
// floating surfaces that would otherwise stack on top of each other.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PiChatCircle, PiPaperPlaneTilt, PiX } from 'react-icons/pi';

const STARTER_PROMPTS = [
  'What needs my attention?',
  "What's the status of my orders?",
  'How does escrow work?',
];

const FALLBACK_REPLY =
  "I'm having trouble answering right now. For anything account-specific, " +
  'our support team can help.';

// Same auto-grow approach as WorkHistoryFields — reset to auto first so a
// shrinking value (e.g. clearing the box after send) recomputes scrollHeight
// from scratch instead of only ever growing.
function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function TypingBubble() {
  return (
    <div className="mr-auto flex items-center gap-1 rounded-2xl rounded-tl-sm bg-light-tertiary px-3.5 py-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-dark-secondary/50 motion-reduce:animate-none"
          style={{ animationDelay: `${i * 120}ms` }}
        />
      ))}
    </div>
  );
}

export default function AskUniFreelancer({ user }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  // Lets page content (e.g. the homepage FAQ's "Ask anything else") open the widget.
  useEffect(() => {
    const openWidget = () => setOpen(true);
    window.addEventListener('askuf:open', openWidget);
    return () => window.removeEventListener('askuf:open', openWidget);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 260);
  }, [open]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    autoResize(inputRef.current);
  }, [input]);

  async function send(question) {
    const text = (question ?? input).trim();
    if (!text || sending) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      const res = await fetch('/api/ai/support-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: text,
          history: nextMessages.slice(0, -1),
        }),
      });
      const data = await res.json().catch(() => ({}));
      const reply = res.ok && data.reply ? data.reply : FALLBACK_REPLY;
      setMessages((cur) => [...cur, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((cur) => [...cur, { role: 'assistant', content: FALLBACK_REPLY }]);
    } finally {
      setSending(false);
    }
  }

  // The inbox composer's Send button lives in the same bottom-right corner,
  // and support chat is one click away on every other page.
  if (!user || pathname.startsWith('/inbox')) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Ask UniFreelancer"
          className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-[100] flex h-14 w-14 phone:hidden items-center justify-center rounded-full bg-dark-primary text-accent-tertiary shadow-accent transition-transform hover:-translate-y-0.5 hover:scale-105 sm:bottom-6 sm:right-6"
        >
          <span className="absolute inset-0 rounded-full border border-accent opacity-50 motion-reduce:hidden" style={{ animation: 'askuf-pulse 2.6s ease-out infinite' }} />
          <PiChatCircle size={24} aria-hidden="true" />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Ask UniFreelancer"
          className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-[100] flex h-[480px] max-h-[calc(100vh-6rem)] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-light-primary bg-white font-body shadow-xl animate-scale-in motion-reduce:animate-none sm:bottom-6 sm:right-6"
        >
          <div className="flex items-center gap-2.5 bg-dark-primary px-4 py-3.5 text-white">
            <PiChatCircle size={17} className="text-accent-tertiary" aria-hidden="true" />
            <span className="font-sans text-sm font-semibold">Ask UniFreelancer</span>
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[12px] font-bold text-accent-tertiary">AI</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              <PiX size={15} />
            </button>
          </div>

          <div ref={threadRef} className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
            {messages.length === 0 && (
              <>
                <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-light-tertiary px-3.5 py-2.5 text-sm leading-relaxed text-body">
                  Hi, I can answer questions about how UniFreelancer works, and look up your own orders, balance, or tax status. Nothing in this chat is saved. What do you need?
                </div>
                <div className="flex flex-wrap gap-2">
                  {STARTER_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      className="rounded-full border border-light-primary px-3 py-1.5 text-xs font-medium text-body transition hover:border-accent hover:bg-accent-muted hover:text-dark-primary"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === 'user'
                    ? 'ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-dark-primary px-3.5 py-2.5 text-sm leading-relaxed text-white'
                    : 'mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-light-tertiary px-3.5 py-2.5 text-sm leading-relaxed text-body'
                }
              >
                {m.content}
              </div>
            ))}
            {sending && <TypingBubble />}
          </div>

          <div className="border-t border-light-primary p-3">
            <div className="flex items-end gap-2 rounded-xl border border-light-primary bg-main-bg px-3 py-1.5 focus-within:border-dark-primary">
              <textarea autoComplete="off" aria-label="Ask a question"
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask a question…"
                disabled={sending}
                className="max-h-28 flex-1 resize-none overflow-y-auto bg-transparent py-1.5 text-sm leading-relaxed text-dark-primary outline-none placeholder:text-muted disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                aria-label="Send"
                className="mb-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-accent text-white transition hover:bg-accent-secondary disabled:opacity-40"
              >
                <PiPaperPlaneTilt size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes askuf-pulse {
          0% { transform: scale(0.92); opacity: 0.55; }
          100% { transform: scale(1.35); opacity: 0; }
        }
      `}</style>
    </>
  );
}
